import React, { useEffect, useRef, useState, useCallback } from 'react';
import { p, tp, useP } from '../i18n/useP';
import { useTranslation } from 'react-i18next';
import {
  getCantons, getChargesSociales, importCanton, copyChargesFromYear,
  updateLigne, createLigne, deleteLigne,
} from '../api/charges_sociales';
import PageHeader from '../components/PageHeader';
import Card from '../components/Card';
import SegmentedToggle from '../components/SegmentedToggle';
import { PaymentChips } from './treasury/tabs/SocialPhasingTab';
import { updateTenant } from '../api/tenants';
import { listLppForYear } from '../api/hr';
import './ChargesSocialesPage.css';

// LPP volontairement HORS de cette page : ce n'est pas un taux uniforme (variable
// par personne selon âge/salaire). Source unique = onglet LPP d'Input People.

const ANNEE_COURANTE = new Date().getFullYear();
const ANNEES = [ANNEE_COURANTE - 1, ANNEE_COURANTE, ANNEE_COURANTE + 1];
const CANTONS = ['BE', 'FR', 'GE', 'JU', 'NE', 'VD', 'VS'];

// ── Cellule nombre éditable inline ──────────────────────────────────────────
function NumCell({ value, field, onSave, decimals = 3, suffix = '' }) {
  const p = useP();
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState('');
  const ref = useRef(null);

  useEffect(() => {
    if (editing && ref.current) { ref.current.focus(); ref.current.select(); }
  }, [editing]);

  function start() {
    setDraft(String(value ?? 0));
    setEditing(true);
  }
  function commit() {
    setEditing(false);
    const v = parseFloat((draft + '').replace(/[\s']/g, '').replace(',', '.')) || 0;
    if (v !== value) onSave({ [field]: v });
  }

  if (editing) {
    return (
      <input
        ref={ref}
        className="cs-cell-input"
        type="text"
        inputMode="decimal"
        value={draft}
        onChange={e => setDraft(e.target.value)}
        onBlur={commit}
        onKeyDown={e => {
          if (e.key === 'Enter') commit();
          if (e.key === 'Escape') setEditing(false);
        }}
      />
    );
  }
  const display =
    value !== null && value !== undefined
      ? Number(value).toLocaleString('de-CH', {
          minimumFractionDigits: decimals,
          maximumFractionDigits: decimals,
        }) + suffix
      : '—';
  return (
    <span className="cs-cell-saisie" onClick={start} title={p('Cliquer pour modifier')}>
      {display}
    </span>
  );
}

// ── Cellule texte éditable inline ────────────────────────────────────────────
function TextCell({ value, field, onSave }) {
  const p = useP();
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState('');
  const ref = useRef(null);

  useEffect(() => {
    if (editing && ref.current) { ref.current.focus(); ref.current.select(); }
  }, [editing]);

  function start() {
    setDraft(value || '');
    setEditing(true);
  }
  function commit() {
    setEditing(false);
    if (draft !== value) onSave({ [field]: draft });
  }

  if (editing) {
    return (
      <input
        ref={ref}
        className="cs-cell-input cs-cell-input--text"
        type="text"
        value={draft}
        onChange={e => setDraft(e.target.value)}
        onBlur={commit}
        onKeyDown={e => {
          if (e.key === 'Enter') commit();
          if (e.key === 'Escape') setEditing(false);
        }}
      />
    );
  }
  return (
    <span className="cs-cell-libelle" onClick={start} title={p('Cliquer pour modifier')}>
      {value || '—'}
    </span>
  );
}

// ── Formatage ─────────────────────────────────────────────────────────────────
function fmt3(v) {
  if (v === null || v === undefined) return '—';
  return Number(v).toLocaleString('de-CH', {
    minimumFractionDigits: 3,
    maximumFractionDigits: 3,
  });
}

// ─────────────────────────────────────────────────────────────────────────────
export default function ChargesSocialesPage({ activeTenant, addToast, embedded, year: yearProp, onYearChange, only = null }) {
  /* `only` rend UNE card (patron de HrPlanningHub) : la section « Assurances
     sociales » se lit en quatre onglets depuis le 2026-09-27, synthèse,
     cotisations de base, assurances supplémentaires, échéancier. */
  const show = (id) => !only || only === id;
  const { t } = useTranslation();
  const p = useP();
  // Embarqué dans HR settings : l'année est pilotée par le picker de la ligne
  // d'onglets (année globale). Autonome : état local.
  const [localAnnee, setLocalAnnee] = useState(ANNEE_COURANTE);
  const annee = embedded && yearProp != null ? yearProp : localAnnee;
  /* Le taux LPP par défaut vit sur le CLIENT, pas sur l'année : c'est un
     réglage, pas une donnée d'exercice. */
  const [lppOn, setLppOn] = useState(true);
  const [lppEmpr, setLppEmpr] = useState('');
  const [lppEmpl, setLppEmpl] = useState('');
  useEffect(() => {
    if (!activeTenant) return;
    setLppOn(activeTenant.lpp_default_on !== false);
    // 7 % employeur et 7 % employé quand rien n'est encore posé (2026-09-27).
    const pct = (v) => (v == null ? '7' : String(Math.round(Number(v) * 10000) / 100));
    setLppEmpr(pct(activeTenant.lpp_default_empr_pct));
    setLppEmpl(pct(activeTenant.lpp_default_empl_pct));
  }, [activeTenant]);
  const saveLpp = async (chg) => {
    const num = (v) => {
      const x = parseFloat(String(v).replace(',', '.'));
      return Number.isFinite(x) ? x / 100 : null;
    };
    const body = {};
    if ('on' in chg) { setLppOn(chg.on); body.lpp_default_on = chg.on; }
    if ('empr' in chg) body.lpp_default_empr_pct = num(chg.empr);
    if ('empl' in chg) body.lpp_default_empl_pct = num(chg.empl);
    const r = await updateTenant(activeTenant.id, body);
    if (r && r.error) addToast && addToast(r.error, 'error');
  };
  const setAnnee = embedded && onYearChange ? onYearChange : setLocalAnnee;
  const [cantons, setCantons] = useState([]);
  const [cantonActif, setCantonActif] = useState(null);
  const [showCantons, setShowCantons] = useState(false);
  /* Le taux LPP MOYEN, pondéré par la rémunération de référence : c'est celui
     que le coût employeur applique aux personnes documentées. Il se lit, il ne
     se tape pas — sauf quand le taux par défaut est éteint. */
  const [lppAvg, setLppAvg] = useState(null);
  useEffect(() => {
    if (!activeTenant || !annee) return;
    listLppForYear(activeTenant.id, annee, 'active')
      .then((r) => {
        const rows = (r && !r.error && (r.data?.rows || r.data)) || [];
        let w = 0; let se = 0; let sl = 0;
        (Array.isArray(rows) ? rows : []).forEach((x) => {
          const sal = Number(x.reference_salary_annual) || 0;
          if (!sal) return;
          w += sal;
          se += sal * (Number(x.lpp_empr_pct) || 0);
          sl += sal * (Number(x.lpp_empl_pct) || 0);
        });
        // Les taux de People sont en DÉCIMAL (0.06 = 6 %), la synthèse parle
        // en pour-cent : sans le facteur, la LPP se lisait 0.060 %.
        setLppAvg(w > 0 ? { empr: (se / w) * 100, empl: (sl / w) * 100 } : null);
      })
      .catch(() => setLppAvg(null));
  }, [activeTenant, annee]);
  const [lignes, setLignes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [importing, setImporting] = useState(false);

  // Charger les cantons disponibles
  useEffect(() => {
    getCantons()
      .then(res => setCantons(res.data || CANTONS))
      .catch(() => setCantons(CANTONS));
  }, []);

  // Charger les lignes du tenant
  const loadLignes = useCallback(() => {
    if (!activeTenant) { setLignes([]); return; }
    setLoading(true);
    getChargesSociales(activeTenant.id, annee)
      .then(res => {
        const data = res.data || [];
        setLignes(data);
        // Détecter le canton actif depuis les données (si présent)
        // On ne peut pas le savoir directement — on laisse l'UI gérer
      })
      .catch(() => addToast(p('Erreur chargement charges sociales'), 'error'))
      .finally(() => setLoading(false));
  }, [activeTenant, annee, addToast, p]);

  useEffect(() => { loadLignes(); }, [loadLignes]);

  // ── Import canton ──────────────────────────────────────────────────────────
  async function handleImportCanton(canton) {
    if (!activeTenant || importing) return;
    if (lignes.length > 0) {
      const ok = window.confirm(
        t('phrases:p_5639b9873b', { count: lignes.length, year: annee, canton }),
      );
      if (!ok) return;
    }
    setImporting(true);
    try {
      const res = await importCanton(activeTenant.id, canton, annee);
      if (res.data) {
        setCantonActif(canton);
        addToast(t('phrases:p_eafc54ea3a', { count: res.data.imported, canton, year: annee }));
        loadLignes();
      } else {
        addToast(res.error || p('Erreur import'), 'error');
      }
    } finally {
      setImporting(false);
    }
  }

  async function handleCopyFromYear(fromYear) {
    if (!activeTenant || importing) return;
    if (lignes.length > 0) {
      addToast(t('phrases:p_c2d60eb207', { year: annee, fromYear }), 'error');
      return;
    }
    setImporting(true);
    try {
      const res = await copyChargesFromYear(activeTenant.id, fromYear, annee);
      if (res.data) {
        addToast(t('phrases:p_b4a5fd1327', { count: res.data.copied, year: res.data.from_year }));
        loadLignes();
      } else {
        addToast(res.error || p('Erreur copie'), 'error');
      }
    } finally {
      setImporting(false);
    }
  }

  // ── Update ligne ───────────────────────────────────────────────────────────
  async function handleUpdateLigne(ligneId, patch) {
    if (!activeTenant) return;
    const res = await updateLigne(activeTenant.id, ligneId, patch);
    if (res.data) {
      setLignes(prev => prev.map(l => l.id === ligneId ? res.data : l));
    } else {
      addToast(res.error || p('Erreur sauvegarde'), 'error');
      loadLignes();
    }
  }

  // ── Ajouter ligne ──────────────────────────────────────────────────────────
  async function handleAddLigne(section) {
    if (!activeTenant) return;
    const res = await createLigne(activeTenant.id, {
      annee,
      section,
      libelle: p('Nouvelle ligne'),
    });
    if (res.data) {
      loadLignes();
      addToast(t('phrases:p_c0e28e6707'));
    } else {
      addToast(res.error || p('Erreur ajout'), 'error');
    }
  }

  // ── Supprimer ligne ────────────────────────────────────────────────────────
  async function handleDeleteLigne(ligneId) {
    if (!activeTenant) return;
    if (!window.confirm(p('Supprimer cette ligne ?'))) return;
    const res = await deleteLigne(activeTenant.id, ligneId);
    if (res.data) {
      setLignes(prev => prev.filter(l => l.id !== ligneId));
      addToast(t('phrases:p_68daf495b6'));
    } else {
      addToast(res.error || p('Erreur suppression'), 'error');
    }
  }

  // ── Sections ───────────────────────────────────────────────────────────────
  const lignesBase = lignes.filter(l => l.section === 'base');
  // La LPP n'est plus une ligne d'assurance (taux moyen trompeur) : elle a sa
  // propre card par personne. On exclut donc toute ligne « LPP » résiduelle.
  const lignesAss  = lignes.filter(l => l.section === 'assurance' && !(l.libelle || '').toLowerCase().includes('lpp'));

  // ── Calcul assurances (taux_empr et taux_empl calculés depuis taux_total + part_empr_pct)
  function calcAssLigne(l) {
    const total   = l.taux_total ?? 0;
    const partEmpr = l.part_empr_pct ?? 0;
    const empr    = total * (partEmpr / 100);
    const empl    = total * (1 - partEmpr / 100);
    return { empr, empl };
  }

  // ── Sous-totaux ────────────────────────────────────────────────────────────
  const stBase = lignesBase.reduce(
    (acc, l) => ({ empr: acc.empr + (l.taux_empr || 0), empl: acc.empl + (l.taux_empl || 0) }),
    { empr: 0, empl: 0 }
  );
  const stAss = lignesAss.reduce((acc, l) => {
    const { empr, empl } = calcAssLigne(l);
    return { empr: acc.empr + empr, empl: acc.empl + empl };
  }, { empr: 0, empl: 0 });

  /* Trois provenances pour le taux LPP, dans cet ordre : le taux STANDARD du
     client s'il est posé, sinon celui CALCULÉ sur les personnes documentées,
     sinon ce qu'on SAISIT ici quand le défaut est éteint. */
  const numPct = (v) => {
    const x = parseFloat(String(v).replace(',', '.'));
    return Number.isFinite(x) ? x : 0;
  };
  const lppLine = lppOn ? { empr: numPct(lppEmpr), empl: numPct(lppEmpl) }
    : (lppAvg || { empr: 0, empl: 0 });
  const lppSrc = lppOn ? p('taux moyen standard') : p('calculé dans People');
  /* L'échéancier de la LPP vit sur sa ligne d'assurance résiduelle, la seule
     qui porte encore ses mois de paiement. */
  const lppPay = lignes.find((l) => l.section === 'assurance' && (l.libelle || '').toLowerCase().includes('lpp'));
  const chips = (l) => (l ? <PaymentChips value={l.payment_months}
                                         onChange={(v) => handleUpdateLigne(l.id, { payment_months: v })} /> : null);

  const totalEmpr = stBase.empr + stAss.empr;
  const totalEmpl = stBase.empl + stAss.empl;

  // ── Guard no tenant ────────────────────────────────────────────────────────
  if (!activeTenant) {
    return (
      <main className="cs-layout">
        <div className="cs-empty">{p('Sélectionne une entreprise.')}</div>
      </main>
    );
  }

  return (
    <main className={`cs-layout${embedded ? ' is-embedded' : ''}`}>
      {/* ── En-tête (uniquement en mode autonome ; embarqué = le hub porte le
             titre et le picker d'année) ── */}
      {!embedded && (
        <PageHeader
          title={p('Charges sociales')}
          actions={(
            <>
              <label className="cs-annee-label">{t('phrases:p_1f910d98d7')}</label>
              <select
                className="cs-annee-select"
                value={annee}
                onChange={e => setAnnee(Number(e.target.value))}
              >
                {ANNEES.map(a => <option key={a} value={a}>{a}</option>)}
              </select>
            </>
          )}
        />
      )}

      {/* ⚠️ LE TAUX LPP PAR DÉFAUT S'ACTIVE (demande d'Olivier, 2026-09-27) :
          une personne sans taux saisi cotisait quand même, à la moyenne des
          autres ou à 7 %. Le repli se choisit désormais, et il se voit. */}
      {/* LA SYNTHÈSE DIT D'OÙ VIENT LE TAUX (demande d'Olivier, 2026-09-27) :
          trois tuiles donnaient le total sans sa composition, et la LPP en
          était absente. Cotisations de base, détail des assurances
          supplémentaires, LPP, total — employeur, employé, total. */}
      {show('synthese') && lignes.length > 0 && (
        <>
        {/* LA SOURCE LPP AU-DESSUS DE LA CARTE (demande d'Olivier, 2026-09-27) :
            libellé au-dessus, bascule dessous, le costume des commandes de page. */}
        <div className="cs-synth-pick">
          <span className="cs-synth-pick__l">{p('Source LPP')}</span>
          <SegmentedToggle size="lg" value={lppOn ? 'std' : 'people'}
                           onChange={(v) => saveLpp({ on: v === 'std' })}
                           options={[{ value: 'people', label: p('Calculé dans People') },
                                     { value: 'std', label: p('Taux moyen standard') }]} />
        </div>
        <Card title={p('Charges sociales')} flush>
          <table className="cs-table cs-synth" data-export-name={p('Charges sociales')}>
            {/* Largeurs déclarées : la table est en disposition fixe, et cinq
                colonnes égales coupaient l'échéancier après juillet. */}
            <colgroup>
              <col />
              <col className="cs-synth-num" /><col className="cs-synth-num" /><col className="cs-synth-num" />
              <col className="cs-synth-pay" />
            </colgroup>
            <thead>
              <tr>
                <th className="cs-th-l" />
                <th>{p('Employeur')}</th>
                <th>{p('Employé')}</th>
                <th>{p('Total')}</th>
                {/* L'ÉCHÉANCIER À CÔTÉ DES TAUX (essai demandé par Olivier,
                    2026-09-27) : on lit ce que coûte une assurance et quand
                    elle se paie sur la même ligne. L'onglet Échéancier reste
                    le temps de juger. */}
                <th className="cs-th-phasing">{p('Échéancier')}</th>
              </tr>
            </thead>
            <tbody>
              <tr className="cs-synth-strong">
                <td className="cs-th-l">{p('Cotisations de base')}</td>
                <td>{fmt3(stBase.empr)} %</td>
                <td>{fmt3(stBase.empl)} %</td>
                <td>{fmt3(stBase.empr + stBase.empl)} %</td>
                <td className="cs-td-phasing cs-synth-src">{p('paiement mensuel')}</td>
              </tr>
              {/* Le groupe s'annonce avant ses lignes, sinon elles flottent
                  sous les cotisations de base sans qu'on sache ce qu'elles
                  sont (constat Olivier, 2026-09-27). */}
              {lignesAss.length > 0 && (
                <tr><td className="cs-th-l cs-synth-grp" colSpan={5}>{t('phrases:p_98377b217f')}</td></tr>
              )}
              {lignesAss.map((l) => {
                const a = calcAssLigne(l);
                return (
                  <tr key={l.id}>
                    <td className="cs-th-l cs-synth-sub">{l.libelle}</td>
                    <td>{fmt3(a.empr)} %</td>
                    <td>{fmt3(a.empl)} %</td>
                    <td>{fmt3(a.empr + a.empl)} %</td>
                    <td className="cs-td-phasing">{chips(l)}</td>
                  </tr>
                );
              })}
              {/* TROIS SOUS-TOTAUX, PUIS LE TOTAL (demande d'Olivier,
                  2026-09-27) : le détail des assurances s'additionnait dans le
                  total sans qu'on voie jamais ce qu'il pèse. */}
              {lignesAss.length > 0 && (
                <tr className="cs-synth-strong">
                  <td className="cs-th-l">{p('Total assurances supplémentaires')}</td>
                  <td>{fmt3(stAss.empr)} %</td>
                  <td>{fmt3(stAss.empl)} %</td>
                  <td>{fmt3(stAss.empr + stAss.empl)} %</td>
                  <td />
                </tr>
              )}
              {/* ⚠️ LE CHOIX DE LA SOURCE VIT DANS L'ENTÊTE (2026-09-27) : posé
                  dans la cellule, il cassait la hauteur de la ligne et la table
                  cessait de se lire d'un regard. C'est un contrôle de BASE DE
                  CALCUL, sa place est l'entête de carte (standard, zone 2) ; la
                  ligne n'en garde que la mention. */}
              <tr className="cs-synth-strong">
                <td className="cs-th-l">
                  {p('LPP')} <span className="cs-synth-src">· {lppSrc}</span>
                </td>
                {lppOn ? (<>
                  <td><input className="cs-synth-in" inputMode="decimal" value={lppEmpr}
                             onChange={(e) => setLppEmpr(e.target.value)}
                             onBlur={() => saveLpp({ empr: lppEmpr })} /></td>
                  <td><input className="cs-synth-in" inputMode="decimal" value={lppEmpl}
                             onChange={(e) => setLppEmpl(e.target.value)}
                             onBlur={() => saveLpp({ empl: lppEmpl })} /></td>
                </>) : (<>
                  <td>{fmt3(lppLine.empr)} %</td>
                  <td>{fmt3(lppLine.empl)} %</td>
                </>)}
                <td>{fmt3(lppLine.empr + lppLine.empl)} %</td>
                <td className="cs-td-phasing">{chips(lppPay)}</td>
              </tr>
              <tr className="cs-synth-total">
                <td className="cs-th-l">{p('Total')}</td>
                <td>{fmt3(totalEmpr + lppLine.empr)} %</td>
                <td>{fmt3(totalEmpl + lppLine.empl)} %</td>
                <td>{fmt3(totalEmpr + totalEmpl + lppLine.empr + lppLine.empl)} %</td>
                <td />
              </tr>
            </tbody>
          </table>
        </Card>
        </>
      )}

      {/* ── Section 1 : Cotisations de base (porte l'import des taux) ── */}
      {show('base') && (<Card title={p('Cotisations de base')} flush>
        {/* Barre d'import des taux — intégrée à la card des cotisations de base */}
        <div className="cs-import">
          {/* UN BOUTON, PAS SEPT (demande d'Olivier, 2026-09-27) : les sept
              cantons occupaient la barre en permanence pour un geste qu'on fait
              une fois l'an. Le canton se choisit dans la liste qui s'ouvre. */}
          <button
            className="cs-canton-btn"
            onClick={() => setShowCantons((v) => !v)}
            disabled={importing}
          >
            {p('Importer les taux')}{cantonActif ? ` · ${cantonActif}` : ''}
          </button>
          {showCantons && CANTONS.map(c => (
            <button
              key={c}
              className={`cs-canton-btn${cantonActif === c ? ' cs-canton-btn--active' : ''}`}
              onClick={() => { setShowCantons(false); handleImportCanton(c); }}
              disabled={importing}
              title={t('phrases:p_3f47e649c9', { canton: c })}
            >
              {c}
            </button>
          ))}
          <button
            className="cs-canton-btn"
            onClick={() => handleCopyFromYear(annee - 1)}
            disabled={importing || lignes.length > 0}
            title={lignes.length > 0
              ? t('phrases:p_c2d60eb207', { year: annee, fromYear: annee - 1 })
              : t('phrases:p_63eac97748', { year: annee - 1 })}
            style={{ marginLeft: 8 }}
          >
            {t('phrases:p_3609a6618e', { year: annee - 1 })}
          </button>
          <button
            className="cs-canton-btn"
            onClick={() => handleCopyFromYear(annee + 1)}
            disabled={importing || lignes.length > 0}
            title={lignes.length > 0
              ? t('phrases:p_c2d60eb207', { year: annee, fromYear: annee + 1 })
              : t('phrases:p_63eac97748', { year: annee + 1 })}
          >
            {t('phrases:p_3609a6618e', { year: annee + 1 })}
          </button>
          {importing && <span className="cs-importing">{p('Import en cours…')}</span>}
        </div>

        {lignesBase.length > 0 ? (
          <table className="cs-table" data-export-name={p('Cotisations de base')}>
            <colgroup>
              <col className="cs-col-libelle" />
              <col className="cs-col-num" />
              <col className="cs-col-num" />
              <col className="cs-col-del" />
            </colgroup>
            <thead>
              <tr>
                <th className="cs-th-libelle">{t('phrases:p_f5485bbae8')}</th>
                <th>{p('Taux employeur (%)')}</th>
                <th>{p('Taux employé (%)')}</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {lignesBase.map(l => (
                <tr key={l.id}>
                  <td className="cs-td-libelle">
                    <TextCell value={l.libelle} field="libelle" onSave={patch => handleUpdateLigne(l.id, patch)} />
                  </td>
                  <td>
                    <NumCell value={l.taux_empr} field="taux_empr" onSave={patch => handleUpdateLigne(l.id, patch)} decimals={3} suffix="%" />
                  </td>
                  <td>
                    <NumCell value={l.taux_empl} field="taux_empl" onSave={patch => handleUpdateLigne(l.id, patch)} decimals={3} suffix="%" />
                  </td>
                  <td>
                    <button className="cs-btn-delete" onClick={() => handleDeleteLigne(l.id)} title={t('common:actions.delete')}>✕</button>
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr className="cs-subtotal">
                <td className="cs-subtotal-label">{p('Sous-total cotisations')}</td>
                <td className="cs-subtotal-val">{fmt3(stBase.empr)}%</td>
                <td className="cs-subtotal-val">{fmt3(stBase.empl)}%</td>
                <td></td>
              </tr>
            </tfoot>
          </table>
        ) : (
          !loading && (
            <div className="cs-empty-inline">
              {t('phrases:p_cffe1b6e73', { year: annee })}
            </div>
          )
        )}
        <div className="cs-section-foot">
          <button className="cs-btn-add" onClick={() => handleAddLigne('base')}>
            {p('+ Ajouter une ligne')}
          </button>
        </div>
      </Card>)}

      {/* ── Section 2 : Assurances supplémentaires (hors LPP) ── */}
      {/* L'encart se montre même vide (2026-09-28) : masqué, il ne laissait
          pas ajouter la première assurance. */}
      {show('supp') && (
        <Card title={t('phrases:p_98377b217f')} flush>
          <table className="cs-table" data-export-name={t('phrases:p_98377b217f')}>
            <colgroup>
                <col className="cs-col-libelle" />
                <col className="cs-col-num" />
                <col className="cs-col-num" />
                <col className="cs-col-num" />
                <col className="cs-col-num" />
                <col className="cs-col-del" />
              </colgroup>
              <thead>
                <tr>
                  <th className="cs-th-libelle">{t('phrases:p_f5485bbae8')}</th>
                  <th>{p('Cotisation total (%)')}</th>
                  <th>{p('Part employeur (%)')}</th>
                  <th className="cs-th-calc">{p('Taux employeur (%)')}</th>
                  <th className="cs-th-calc">{p('Taux employé (%)')}</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {lignesAss.map(l => {
                  const { empr, empl } = calcAssLigne(l);
                  return (
                    <tr key={l.id}>
                      <td className="cs-td-libelle">
                        <TextCell value={l.libelle} field="libelle" onSave={patch => handleUpdateLigne(l.id, patch)} />
                      </td>
                      <td>
                        <NumCell value={l.taux_total} field="taux_total" onSave={patch => handleUpdateLigne(l.id, patch)} decimals={3} suffix="%" />
                      </td>
                      <td>
                        <NumCell value={l.part_empr_pct} field="part_empr_pct" onSave={patch => handleUpdateLigne(l.id, patch)} decimals={1} suffix="%" />
                      </td>
                      <td className="cs-td-calc">{fmt3(empr)}%</td>
                      <td className="cs-td-calc">{fmt3(empl)}%</td>
                      <td className="cs-td-actions">
                        <button className="cs-btn-delete" onClick={() => handleDeleteLigne(l.id)} title={t('common:actions.delete')}>✕</button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
              <tfoot>
                <tr className="cs-subtotal">
                  <td className="cs-subtotal-label" colSpan={3}>{p('Sous-total assurances')}</td>
                  <td className="cs-subtotal-val">{fmt3(stAss.empr)}%</td>
                  <td className="cs-subtotal-val">{fmt3(stAss.empl)}%</td>
                  <td></td>
                </tr>
              </tfoot>
            </table>
          <div className="cs-section-foot">
            <button className="cs-btn-add" onClick={() => handleAddLigne('assurance')}>
              {p('+ Ajouter une ligne')}
            </button>
          </div>
        </Card>
      )}

      {/* Le phasing du paiement (échéancier cash des assurances) a déménagé dans
          Planning → Set up → Phasing charges sociales : ici on ne garde que les
          taux (coût). Découplage coût/cash, comme les coûts indirects. */}

      {loading && <p className="cs-loading">{t('common:status.loading')}</p>}
    </main>
  );
}
